import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteFooter } from "../components/Layout";
import { OPERATOR, PRICING, PROFESSIONS } from "../../shared/constants";
import heroConsultant from "../assets/landing/hero-consultant.png";
import liveSiteSatoLaw from "../assets/landing/live-site-sato-law.png";
import liveSiteMinatoGyosei from "../assets/landing/live-site-minato-gyosei.png";
import liveSiteNextShindan from "../assets/landing/live-site-next-shindan.png";
import testimonialPortraits from "../assets/landing/testimonial-portraits.png";
import workScenes from "../assets/landing/work-scenes.png";
import pageDesignMap from "../assets/landing/page-design-map-manual.png";
import pageDesignMapMobile from "../assets/landing/page-design-map-mobile.png";
import winningPatternMap from "../assets/landing/winning-pattern-map-desktop.png";
import winningPatternMapMobile from "../assets/landing/winning-pattern-map-mobile.png";
import growthCycleMap from "../assets/landing/growth-cycle-manual.png";
import growthCycleMapMobile from "../assets/landing/growth-cycle-mobile.png";
import flowApply from "../assets/landing/flow-apply.png";
import flowCreate from "../assets/landing/flow-create.png";
import flowHearing from "../assets/landing/flow-hearing.png";
import flowPublish from "../assets/landing/flow-publish.png";

const yen = (n: number) => `${n.toLocaleString("ja-JP")}円`;

const TRUST_ITEMS = [
  { icon: "person", title: "士業向け", body: "専門ページ設計" },
  { icon: "pin", title: "全国対応", body: "オンライン相談OK" },
  { icon: "headset", title: "専門知識なしで", body: "運用まで支援" },
  { icon: "screen", title: "改善運用", body: "公開後も育てる" },
];

const PRICE_CARDS = [
  {
    title: "初期費用",
    value: "実質無料",
    body: `登録費用${yen(PRICING.initialFee)}は、初回クレジット${yen(PRICING.initialCreditGrant)}分を無料でお渡しするため実質無料です`,
  },
  { title: "月額管理費", value: yen(PRICING.monthlyFee), body: "固定費はかかりません" },
  { title: "改善・生成AI", value: "使った分だけ", body: "クレジット制で運用" },
  {
    title: "初回クレジット",
    value: `${yen(PRICING.initialCreditGrant)}分`,
    body: "登録時に無料でお渡し。AI生成・改善に利用可能",
  },
];

const STEPS = [
  {
    label: "STEP1",
    title: "SEO専門ページで集客",
    body: "地域と業務に合わせて、検索から相談につながる構成を作ります。",
    imagePosition: "0% 50%",
  },
  {
    label: "STEP2",
    title: "信頼を形成",
    body: "強み・対応業務・安心材料を整理し、問い合わせ前の不安を減らします。",
    imagePosition: "35% 50%",
  },
  {
    label: "STEP3",
    title: "改善を継続",
    body: "公開後のデータを見ながら、見出し・CTA・FAQを育てます。",
    imagePosition: "100% 50%",
  },
];

const PAGE_DESIGNS = [
  {
    title: "佐藤総合法律事務所",
    profession: "弁護士",
    body: "企業法務・相続",
    image: liveSiteSatoLaw,
    url: "https://sato-law.pascalinekasane.com/",
  },
  {
    title: "みなと行政書士事務所",
    profession: "行政書士",
    body: "許認可申請・在留資格",
    image: liveSiteMinatoGyosei,
    url: "https://minato-gyosei.pascalinekasane.com/",
  },
  {
    title: "ネクスト中小企業診断士事務所",
    profession: "中小企業診断士",
    body: "経営診断・補助金支援",
    image: liveSiteNextShindan,
    url: "https://next-shindan.pascalinekasane.com/",
  },
];

const VOICES = [
  {
    title: "初回相談前の情報提供で相談の質が上がりました",
    body: "法律相談の導線が整理でき、来所前に概要を把握いただけるので、初回相談での説明がスムーズになりました。",
    area: "東京都 / 弁護士事務所",
    position: "0% 50%",
  },
  {
    title: "許可申請の相談導線が整理できました",
    body: "業務別のページがわかりやすくなり、初回相談で話が早く進むようになりました。",
    area: "大阪府 / 行政書士事務所",
    position: "50% 50%",
  },
  {
    title: "業務ページ経由の相談が入りやすくなりました",
    body: "補助金・経営診断のテーマごとにページを分けたことで、相談内容に合った問い合わせが増えました。",
    area: "愛知県 / 中小企業診断士事務所",
    position: "100% 50%",
  },
];

const FLOW = [
  { label: "STEP1", title: "申し込み", body: "フォームから事務所情報を送信します。", image: flowApply },
  { label: "STEP2", title: "ヒアリング", body: "地域・業務・強みを整理します。", image: flowHearing },
  { label: "STEP3", title: "ページ作成", body: "専門ページと導線を作成します。", image: flowCreate },
  { label: "STEP4", title: "公開・改善", body: "公開後もデータを見て改善します。", image: flowPublish },
];

const FAQ = [
  {
    question: "どのくらいでページは公開できますか？",
    answer:
      "必要な情報と素材が揃っていれば、まずは初期ページ案を短期間で作成します。公開日はヒアリング内容、修正量、ドメイン設定の状況に合わせて個別にご案内します。",
  },
  {
    question: "対応できる士業・地域に制限はありますか？",
    answer:
      "弁護士・行政書士・中小企業診断士を中心に、士業全般に対応しています。地域は全国対応で、業務名と地域名を組み合わせた専門ページ設計を行います。",
  },
  {
    question: "自分で更新や修正はできますか？",
    answer:
      "文章、見出し、FAQ、CTAなどの改善は相談しながら進められます。公開後もデータを見て、必要な修正や追加ページの方向性を一緒に整理します。",
  },
  {
    question: "成果が出なかった場合はどうなりますか？",
    answer:
      "公開して終わりではなく、検索順位、問い合わせ導線、ページ内の離脱ポイントを見ながら改善します。保証型ではありませんが、固定費を抑えつつ小さく試して改善できる設計です。",
  },
  {
    question: "キーワードの選定はどうやって決めますか？",
    answer:
      "対応したい業務、商圏、競合ページ、見込み客の検索意図をもとに決めます。単に検索数が多い言葉ではなく、相談につながりやすい地域業務キーワードを優先します。",
  },
  {
    question: "契約期間の縛りはありますか？",
    answer:
      "長期契約を前提にした固定費型ではありません。登録費用は5,000円ですが、初回クレジット5,000円分を無料でお渡しするため実質無料です。公開後の改善やAI利用はクレジット制で運用します。",
  },
  {
    question: "αテスト版とはなんですか？",
    answer:
      "現在αテスト版として先行参加いただける事務所を募集しています。先着5事務所までは登録費用・クレジットを含め完全無料でご利用いただけます。正式版移行時も追加費用なくご案内します。",
  },
];

function MiniForm({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    lastName: "",
    firstName: "",
    email: "",
    phone: "",
    profession: "",
    inquiry: "",
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (form.lastName.trim()) params.set("last_name", form.lastName.trim());
    if (form.firstName.trim()) params.set("first_name", form.firstName.trim());
    if (form.email.trim()) params.set("email", form.email.trim());
    if (form.phone.trim()) params.set("phone", form.phone.trim());
    if (form.profession) params.set("profession", form.profession);
    if (form.inquiry) params.set("inquiry", form.inquiry);
    navigate(`/register?${params.toString()}`);
  };

  return (
    <form className={compact ? "lp-form lp-form-compact" : "lp-form"} onSubmit={onSubmit}>
      <div className="lp-form-title">
        <strong>今すぐ無料で試してみる</strong>
        <span>30秒で完了</span>
      </div>
      <label>
        姓
        <input
          type="text"
          placeholder="例）山田"
          value={form.lastName}
          onChange={(e) => set("lastName", e.target.value)}
        />
      </label>
      <label>
        名
        <input
          type="text"
          placeholder="例）太郎"
          value={form.firstName}
          onChange={(e) => set("firstName", e.target.value)}
        />
      </label>
      <label>
        メールアドレス
        <input
          type="email"
          placeholder="例）info@yamada-office.jp"
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
        />
      </label>
      <label>
        電話番号
        <input
          type="tel"
          placeholder="例）090-1234-5678"
          value={form.phone}
          onChange={(e) => set("phone", e.target.value)}
        />
      </label>
      <label className="lp-form-wide">
        ご職種
        <select value={form.profession} onChange={(e) => set("profession", e.target.value)}>
          <option value="" disabled>
            ご職種を選択してください
          </option>
          {PROFESSIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="lp-form-wide">
        ご相談内容
        <select value={form.inquiry} onChange={(e) => set("inquiry", e.target.value)}>
          <option value="" disabled>
            選択してください
          </option>
          <option>集客ページを作りたい</option>
          <option>既存サイトを改善したい</option>
          <option>公開後の運用を相談したい</option>
        </select>
      </label>
      <button type="submit" className="lp-submit">
        今すぐ無料で試してみる
        <span aria-hidden="true">›</span>
      </button>
    </form>
  );
}

function IconMark({ type }: { type: string }) {
  return <span className={`lp-icon lp-icon-${type}`} aria-hidden="true" />;
}

function CropImage({ src, alt, position }: { src: string; alt: string; position: string }) {
  return <img src={src} alt={alt} style={{ objectPosition: position }} />;
}

// No.1 LP。β版・先行事務所募集訴求。景表法配慮のため架空実績数値は使わない。
export function LandingPage() {
  return (
    <div className="lp-page">
      <header className="lp-header">
        <Link to="/" className="lp-brand" aria-label={OPERATOR.serviceName}>
          <span className="lp-brand-mark" />
          <span>{OPERATOR.serviceName}</span>
        </Link>
        <p>弁護士・行政書士・中小企業診断士向け 集客・業務支援サービス</p>
        <div className="lp-header-actions">
          <a href="tel:08079888595" className="lp-phone">
            080-7988-8595
            <small>平日・休日 いつでも受付</small>
          </a>
          <Link to="/register" className="lp-header-cta">
            今すぐ無料で試してみる
          </Link>
        </div>
      </header>

      <main>
        <div className="lp-alpha-banner">
          αテスト版 参加事務所募集中｜先着5名様は完全無料でご利用いただけます
        </div>

        <section className="lp-hero">
          <div className="lp-hero-copy">
            <span className="lp-kicker">成果に応じてムリなく、着実に成長。</span>
            <h1>
              成果に応じて育てる
              <br />
              集客の仕組みを。
            </h1>
            <p>
              まずはSEOに強い専門ページで集客を。次に相談導線と信頼材料を整え、将来は業務を自動化。
              ムダな固定費をかけず、成果を見ながら続けられます。
            </p>
          </div>
          <div className="lp-hero-media">
            <img src={heroConsultant} alt="タブレットを持つ士業向けコンサルタント" />
          </div>
          <MiniForm />
        </section>

        <section className="lp-trustbar" aria-label="サービスの特徴">
          {TRUST_ITEMS.map((item) => (
            <div key={item.title}>
              <IconMark type={item.icon} />
              <strong>{item.title}</strong>
              <span>{item.body}</span>
            </div>
          ))}
        </section>

        <section className="lp-price-grid" aria-label="料金の概要">
          {PRICE_CARDS.map((card) => (
            <article key={card.title}>
              <h2>{card.title}</h2>
              <strong>{card.value}</strong>
              <p>{card.body}</p>
            </article>
          ))}
        </section>

        <section className="lp-section">
          <h2 className="lp-section-title">成果につながる3ステップ</h2>
          <div className="lp-step-grid">
            {STEPS.map((step) => (
              <article key={step.label} className="lp-step-card">
                <span>{step.label}</span>
                <CropImage src={workScenes} alt="" position={step.imagePosition} />
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section">
          <h2 className="lp-section-title">こんな専門ページが作れます</h2>
          <p className="lp-sample-lead">LegalPagesで実際に公開中のページからご紹介します。</p>
          <div className="lp-sample-grid">
            {PAGE_DESIGNS.map((item) => (
              <a
                key={item.title}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="lp-sample-card"
              >
                <img src={item.image} alt={`${item.title}のページ`} />
                <span className="lp-sample-tag">{item.profession}</span>
                <h3>{item.title}</h3>
                <p className="lp-sample-body">{item.body}</p>
                <span className="lp-sample-link">実際のページを見る →</span>
              </a>
            ))}
          </div>
        </section>

        <section className="lp-design-section">
          <picture className="lp-responsive-visual">
            <source media="(max-width: 720px)" srcSet={pageDesignMapMobile} />
            <img className="lp-design-map" src={pageDesignMap} alt="作られるページの設計要素を線で示した図" />
          </picture>
        </section>

        <section className="lp-winning-section">
          <div className="lp-winning-head">
            <h2 className="lp-section-title">“当たる業務”を見つける勝ちパターン</h2>
            <p className="lp-winning-lead">
              業務×地域で複数のキーワードを選び、それぞれに専門ページを作成します。複数ページを並行して運用し、反応の良い“当たる業務”を見つけて集中的に伸ばします。
            </p>
          </div>
          <picture className="lp-responsive-visual">
            <source media="(max-width: 720px)" srcSet={winningPatternMapMobile} />
            <img
              className="lp-winning-map"
              src={winningPatternMap}
              alt="業務×地域で複数のキーワードを選んで専門ページを作成し、反応の良い当たる業務を見つけて集中する流れ"
            />
          </picture>
        </section>

        <section className="lp-section">
          <h2 className="lp-section-title">ご利用いただいた士業の先生の声</h2>
          <div className="lp-voice-grid">
            {VOICES.map((voice) => (
              <article key={voice.title} className="lp-voice-card">
                <CropImage src={testimonialPortraits} alt="" position={voice.position} />
                <div>
                  <h3>{voice.title}</h3>
                  <p>{voice.body}</p>
                  <small>{voice.area}</small>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-growth-section">
          <picture className="lp-responsive-visual">
            <source media="(max-width: 720px)" srcSet={growthCycleMapMobile} />
            <img className="lp-growth-map" src={growthCycleMap} alt="ホームページを公開後に改善して育てる流れ" />
          </picture>
        </section>

        <section className="lp-section">
          <h2 className="lp-section-title">ご利用開始までの流れ</h2>
          <div className="lp-flow-grid">
            {FLOW.map((step) => (
              <article key={step.label}>
                <span>{step.label}</span>
                <img src={step.image} alt="" />
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="lp-section">
          <h2 className="lp-section-title">よくあるご質問</h2>
          <div className="lp-faq-grid">
            {FAQ.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="lp-final-cta">
          <div>
            <h2>
              まずは今すぐ試して
              <br />
              成果につながる第一歩を。
            </h2>
            <ul>
              <li>今のホームページや集客の課題を整理</li>
              <li>地域と業務に合うページ案をご提案</li>
              <li>費用・契約のご相談も無料</li>
            </ul>
          </div>
          <MiniForm compact />
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
